/**
 * Integration Configuration Page
 * Admin-only page for managing third-party API credentials
 */

import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import {
  Settings, Mail, Users, Cloud, Plane, CheckCircle2, XCircle, AlertCircle,
  Loader2, RefreshCw, ExternalLink, Eye, EyeOff, Trash2, TestTube, Power,
  Shield, Key, Globe, Clock
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Service icons and colors
const SERVICE_CONFIG = {
  sendgrid: { icon: Mail, color: 'text-blue-400', bgColor: 'bg-blue-500/10' },
  ldap: { icon: Users, color: 'text-purple-400', bgColor: 'bg-purple-500/10' },
  cloudinary: { icon: Cloud, color: 'text-cyan-400', bgColor: 'bg-cyan-500/10' },
  iata: { icon: Plane, color: 'text-amber-400', bgColor: 'bg-amber-500/10' }
};

// Form fields per service
const FORM_FIELDS = {
  sendgrid: [
    { name: 'api_key', label: 'API Key', type: 'password', required: true },
    { name: 'from_email', label: 'From Email', type: 'email', required: true },
    { name: 'from_name', label: 'From Name', type: 'text', required: false }
  ],
  ldap: [
    { name: 'server_url', label: 'Server URL', type: 'text', required: true },
    { name: 'base_dn', label: 'Base DN', type: 'text', required: true },
    { name: 'bind_dn', label: 'Bind DN', type: 'text', required: true },
    { name: 'bind_password', label: 'Bind Password', type: 'password', required: true }
  ],
  cloudinary: [
    { name: 'cloud_name', label: 'Cloud Name', type: 'text', required: true },
    { name: 'api_key', label: 'API Key', type: 'text', required: true },
    { name: 'api_secret', label: 'API Secret', type: 'password', required: true }
  ],
  iata: [
    { name: 'api_key', label: 'API Key', type: 'password', required: true },
    { name: 'organization_id', label: 'Organization ID', type: 'text', required: true },
    { name: 'iata_code', label: 'IATA Code', type: 'text', required: true },
    { name: 'environment', label: 'Environment', type: 'select', options: ['sandbox', 'production'], required: true }
  ]
};

export default function IntegrationConfigPage() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [integrations, setIntegrations] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [showConfigDialog, setShowConfigDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [testing, setTesting] = useState(null);
  const [activating, setActivating] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showPasswords, setShowPasswords] = useState({});
  const [formData, setFormData] = useState({});

  const getApiHeaders = () => ({
    'Authorization': `Bearer ${localStorage.getItem('token')}`,
    'Content-Type': 'application/json'
  });

  const fetchIntegrations = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/integration-config/`, {
        headers: getApiHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setIntegrations(data.integrations || []);
      }
    } catch (error) {
      console.error('Failed to fetch integrations:', error);
      toast.error('Failed to load integrations');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchIntegrations();
  }, [fetchIntegrations]);

  const handleConfigureService = async (service) => {
    setSelectedService(service);
    if (service.is_configured) {
      try {
        const response = await fetch(`${API_URL}/api/integration-config/${service.service_name}`, {
          headers: getApiHeaders()
        });
        if (response.ok) {
          const data = await response.json();
          const cleanConfig = { ...data.config };
          Object.keys(cleanConfig).forEach(key => {
            if (cleanConfig[key]?.includes?.('*')) cleanConfig[key] = '';
          });
          setFormData(cleanConfig);
        }
      } catch (error) {
        console.error('Failed to fetch config:', error);
      }
    } else {
      setFormData({});
    }
    setShowConfigDialog(true);
  };

  const handleSaveConfig = async () => {
    if (!selectedService) return;
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/integration-config/${selectedService.service_name}`, {
        method: 'PUT',
        headers: getApiHeaders(),
        body: JSON.stringify(formData)
      });
      if (response.ok) {
        toast.success('Configuration saved');
        setShowConfigDialog(false);
        fetchIntegrations();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save');
      }
    } catch (error) {
      toast.error('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async (serviceName) => {
    setTesting(serviceName);
    try {
      const response = await fetch(`${API_URL}/api/integration-config/${serviceName}/test`, {
        method: 'POST',
        headers: getApiHeaders()
      });
      const data = await response.json();
      if (data.success) {
        toast.success(data.message || 'Test successful');
      } else {
        toast.error(data.message || 'Test failed');
      }
      fetchIntegrations();
    } catch (error) {
      toast.error('Failed to test connection');
    } finally {
      setTesting(null);
    }
  };

  const handleToggleActivation = async (serviceName, currentActive) => {
    setActivating(serviceName);
    try {
      const response = await fetch(`${API_URL}/api/integration-config/${serviceName}/activate`, {
        method: 'POST',
        headers: getApiHeaders(),
        body: JSON.stringify({ active: !currentActive })
      });
      if (response.ok) {
        toast.success(`Integration ${!currentActive ? 'activated' : 'deactivated'}`);
        fetchIntegrations();
      }
    } catch (error) {
      toast.error('Failed to change status');
    } finally {
      setActivating(null);
    }
  };

  const handleDeleteConfig = async () => {
    if (!selectedService) return;
    try {
      const response = await fetch(`${API_URL}/api/integration-config/${selectedService.service_name}`, {
        method: 'DELETE',
        headers: getApiHeaders()
      });
      if (response.ok) {
        toast.success('Configuration deleted');
        setShowDeleteDialog(false);
        setSelectedService(null);
        fetchIntegrations();
      }
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const renderStatusBadge = (integration) => {
    if (!integration.is_configured) {
      return <Badge variant="outline" className="bg-slate-500/10 text-slate-400">Not Configured</Badge>;
    }
    if (integration.is_active) {
      return <Badge className="bg-green-500/20 text-green-400">Active</Badge>;
    }
    if (integration.test_status === 'success') {
      return <Badge className="bg-blue-500/20 text-blue-400">Ready</Badge>;
    }
    if (integration.test_status === 'failed') {
      return <Badge className="bg-red-500/20 text-red-400">Failed</Badge>;
    }
    return <Badge className="bg-amber-500/20 text-amber-400">Pending Test</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="integration-config-loading">
        <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="integration-config-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <Settings className="w-8 h-8 text-blue-400" />
            {t('integrations.title')}
          </h1>
          <p className="text-slate-400 mt-1">{t('integrations.subtitle')}</p>
        </div>
        <Button variant="outline" onClick={fetchIntegrations} data-testid="refresh-btn">
          <RefreshCw className="w-4 h-4 mr-2" />
          {t('common.refresh')}
        </Button>
      </div>

      {/* Info Alert */}
      <Alert className="bg-blue-500/10 border-blue-500/30">
        <Shield className="w-4 h-4 text-blue-400" />
        <AlertDescription className="text-slate-300">
          <strong>{t('common.info')}:</strong> {t('integrations.credentials')}
        </AlertDescription>
      </Alert>

      {/* Integrations Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {integrations.map((integration) => {
          const config = SERVICE_CONFIG[integration.service_name] || {};
          const Icon = config.icon || Settings;
          
          return (
            <Card 
              key={integration.service_name}
              className="bg-slate-800/50 border-slate-700 hover:border-slate-600 transition-colors"
              data-testid={`integration-card-${integration.service_name}`}
            >
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${config.bgColor}`}>
                      <Icon className={`w-6 h-6 ${config.color}`} />
                    </div>
                    <div>
                      <CardTitle className="text-white text-lg">{integration.display_name}</CardTitle>
                      <CardDescription className="text-sm">{integration.description}</CardDescription>
                    </div>
                  </div>
                  {renderStatusBadge(integration)}
                </div>
              </CardHeader>
              
              <CardContent className="space-y-4">
                {integration.is_configured && (
                  <div className="grid grid-cols-2 gap-2 p-3 rounded-lg bg-slate-900/50 text-sm">
                    <div className="flex items-center gap-2 text-slate-400">
                      <Clock className="w-3 h-3" />
                      {t('common.lastUpdated')}:
                    </div>
                    <span className="text-slate-200">
                      {integration.config_updated_at ? new Date(integration.config_updated_at).toLocaleDateString() : 'Never'}
                    </span>
                    <div className="flex items-center gap-2 text-slate-400">
                      <TestTube className="w-3 h-3" />
                      {t('integrations.test')}:
                    </div>
                    <span className="text-slate-200">
                      {integration.last_tested ? new Date(integration.last_tested).toLocaleDateString() : 'Never'}
                    </span>
                  </div>
                )}

                {integration.docs_url && (
                  <a 
                    href={integration.docs_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm text-blue-400 hover:text-blue-300"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Documentation
                  </a>
                )}

                <Separator className="bg-slate-700" />

                <div className="flex flex-wrap gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => handleConfigureService(integration)}
                    data-testid={`configure-${integration.service_name}`}
                  >
                    <Key className="w-4 h-4 mr-2" />
                    {integration.is_configured ? 'Edit' : 'Configure'}
                  </Button>
                  
                  {integration.is_configured && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleTestConnection(integration.service_name)}
                        disabled={testing === integration.service_name}
                        data-testid={`test-${integration.service_name}`}
                      >
                        {testing === integration.service_name ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <TestTube className="w-4 h-4 mr-2" />
                        )}
                        Test
                      </Button>

                      <Button
                        variant={integration.is_active ? "destructive" : "default"}
                        size="sm"
                        onClick={() => handleToggleActivation(integration.service_name, integration.is_active)}
                        disabled={activating === integration.service_name}
                        data-testid={`activate-${integration.service_name}`}
                      >
                        {activating === integration.service_name ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Power className="w-4 h-4 mr-2" />
                        )}
                        {integration.is_active ? 'Deactivate' : 'Activate'}
                      </Button>

                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        onClick={() => { setSelectedService(integration); setShowDeleteDialog(true); }}
                        data-testid={`delete-${integration.service_name}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Configuration Dialog */}
      <Dialog open={showConfigDialog} onOpenChange={setShowConfigDialog}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedService && SERVICE_CONFIG[selectedService.service_name] && (
                <>
                  {(() => {
                    const Icon = SERVICE_CONFIG[selectedService.service_name].icon;
                    return <Icon className={`w-5 h-5 ${SERVICE_CONFIG[selectedService.service_name].color}`} />;
                  })()}
                </>
              )}
              Configure {selectedService?.display_name}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Enter API credentials. Sensitive fields will be encrypted.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {selectedService && FORM_FIELDS[selectedService.service_name]?.map((field) => {
              const isPassword = field.type === 'password';
              const showPassword = showPasswords[field.name];

              if (field.type === 'select') {
                return (
                  <div key={field.name} className="space-y-2">
                    <Label className="text-slate-200">{field.label} {field.required && <span className="text-red-400">*</span>}</Label>
                    <Select value={formData[field.name] || ''} onValueChange={(v) => setFormData({...formData, [field.name]: v})}>
                      <SelectTrigger className="bg-slate-700 border-slate-600"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {field.options.map(opt => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                );
              }

              return (
                <div key={field.name} className="space-y-2">
                  <Label className="text-slate-200">{field.label} {field.required && <span className="text-red-400">*</span>}</Label>
                  <div className="relative">
                    <Input
                      type={isPassword && !showPassword ? 'password' : 'text'}
                      value={formData[field.name] || ''}
                      onChange={(e) => setFormData({...formData, [field.name]: e.target.value})}
                      className="bg-slate-700 border-slate-600 pr-10"
                    />
                    {isPassword && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="absolute right-0 top-0 h-full px-3 text-slate-400 hover:text-white"
                        onClick={() => setShowPasswords({...showPasswords, [field.name]: !showPassword})}
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowConfigDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveConfig} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="text-red-400 flex items-center gap-2">
              <Trash2 className="w-5 h-5" />
              Delete Configuration
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Are you sure you want to delete the {selectedService?.display_name} configuration?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDeleteConfig}>
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
